import { useMemo, useState } from "react";
import { makeEconomy, fuelLogsForCar, type CanonicalStore, type Car, type FuelLog } from "../../core/index.ts";
import { FuelLogForm } from "../forms/FuelLogForm.tsx";
import { BackRow } from "../forms/Modal.tsx";
import { LogRow } from "./FuelView.tsx";
import { PeriodFilter, extractPeriods, filterByPeriod } from "./InsightsView.tsx";

/** Full-screen list of every fill-up / charge for a car. Tap a row to edit. */
export function FillupsView({ store, car, save, onBack }: {
  store: CanonicalStore; car: Car; save: (s: CanonicalStore) => void; onBack: () => void;
}) {
  const economy = makeEconomy(store.profile.unitSystem === "metric");
  const currency = store.profile.displayCurrency;
  const isEV = (car.fuelType ?? "").toLowerCase() === "electric";
  const allLogs = fuelLogsForCar(store.fuelLogs, car.id).sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
  const [editing, setEditing] = useState<FuelLog | null>(null);

  const [period, setPeriod] = useState("all");
  const { months, years } = useMemo(() => extractPeriods(allLogs.map((l) => l.date)), [allLogs]);
  const filteredLogs = useMemo(() => filterByPeriod(allLogs, (l) => l.date, period), [allLogs, period]);

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onBack} />
        <div className="screen-title">{isEV ? "ALL CHARGES" : "ALL FILL-UPS"}</div>
        <div className="eyebrow amber" style={{ marginBottom: 14 }}>
          {filteredLogs.length} {isEV ? (filteredLogs.length === 1 ? "CHARGE" : "CHARGES") : (filteredLogs.length === 1 ? "FILL-UP" : "FILL-UPS")} · <span className="muted">{car.model}</span>
        </div>

        {allLogs.length > 0 && (
          <PeriodFilter period={period} setPeriod={setPeriod} months={months} years={years} total={allLogs.length} />
        )}

        {filteredLogs.length === 0 && <div className="panel mono muted" style={{ fontSize: 12 }}>No entries yet.</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
          {filteredLogs.map((l) => (
            <div key={l.id} style={{ cursor: "pointer" }} onClick={() => setEditing(l)}>
              <LogRow log={l} economy={economy} currency={currency} allLogs={allLogs} />
            </div>
          ))}
        </div>
      </div>

      {editing && <FuelLogForm store={store} car={car} editing={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
